/**
 * Sign — per-tab sign-in flow position, plus the sign-flow action channel.
 *
 * signFlowState is persisted to sessionStorage so an accidental navigation
 * away and back returns to the exact screen with email and invite context
 * refilled — never the one-time code: a credential, and the Privacy Policy
 * states flatly it is never stored in clear text, so a restore always lands
 * with an empty code input rather than resuming a partially-typed one.
 * Cleared by sign-in.view.ts at: account created (new user), returning-user
 * sign-in success, and "Use a different email" (onCodeBack).
 *
 * signAction$/signResult$ carry the flow's I/O intent: sign-in.view.ts
 * composes a zero-I/O action here; session.sync.ts (web-side) is the sole
 * subscriber, performing the Firebase Auth + Firestore work and reconciling
 * the outcome back keyed by requestId. `uid`/`displayName` on the result
 * carry the Firebase-Auth-resolved identity forward — the view has no other
 * way to read `auth.currentUser` once the write is dirty-I/O-split out.
 */
import { state } from "@mhmo91/schmancy/state";
import type { SendOTP } from "@momo/types";
import { Subject } from "rxjs";

/** The display-only invite projection sendOTP resolves — see
 *  sendOTP.action.ts. Re-derived from the wire contract, never a parallel
 *  hand-authored shape. */
export type InviteContext = NonNullable<SendOTP.SendOTPOutput["invite"]>;

export interface SignFlowState {
	screen: "email" | "code" | "consent";
	email: string;
	invite: InviteContext | null;
	/** The verifyOTP-proven identity (Firebase-Auth uid + resolved display
	 *  name), carried through the consent step so a remount there — a reload,
	 *  a route re-entry, any app-shell re-render between code-verify and
	 *  consent-accept — doesn't strand a mid-consent invitee whose OTP the
	 *  server already consumed. Cleared on every reset back to the email
	 *  screen so a stale identity never leaks into a fresh sign-in. */
	pendingUid?: string;
	pendingDisplayName?: string;
	/** The organization verifyOTP opened, saved onto the record the consent
	 *  step creates. */
	pendingWorkspaceId?: string;
}

export const signFlowState = state<SignFlowState>("auth/sign-flow").session({
	screen: "email",
	email: "",
	invite: null,
});

// Dropping `code` from the type above is not what removes it from a tab that
// already stored one: schmancy's restore deliberately retains stored keys its
// initial value has no opinion about — a newer build's field must survive an
// older build's repair — so a code persisted before this shape is still
// sitting in this tab's sessionStorage, and the Privacy Policy states flatly
// that it never is. Rewriting the state to exactly the fields declared above,
// once the restore has settled, is what actually clears it: schmancy persists
// on write, so the new bytes replace the old ones with no code in them.
signFlowState.ready.then(() => {
	const {
		screen,
		email,
		invite,
		pendingUid,
		pendingDisplayName,
		pendingWorkspaceId,
	} = signFlowState.value;
	signFlowState.replace({
		screen,
		email,
		invite,
		// Spread rather than assigned: under exactOptionalPropertyTypes an
		// explicit `undefined` is not the same as an absent optional field.
		...(pendingUid === undefined ? {} : { pendingUid }),
		...(pendingDisplayName === undefined ? {} : { pendingDisplayName }),
		...(pendingWorkspaceId === undefined ? {} : { pendingWorkspaceId }),
	});
});

declare module "@mhmo91/schmancy/state" {
	interface SchmancyStateRegistry {
		"auth/sign-flow": SignFlowState;
	}
}

// ── sign-flow action channel ────────────────────────────────────────────────

export type SignAction =
	/** The email step's first ask: would this address be mailed a code?
	 *  Answered `known`; unknown opens the no-account screen and no code is
	 *  asked for. */
	| { kind: "resolveEmail"; requestId: string; email: string }
	| { kind: "sendOtp"; requestId: string; email: string }
	| { kind: "verifyOtp"; requestId: string; email: string; code: string }
	| {
			kind: "createAccount";
			requestId: string;
			uid: string;
			email: string;
			displayName: string;
			/** The organization verifyOTP opened; null when this flow never
			 *  learned one (a consent resumed from an older tab). */
			workspaceId: string | null;
			tosVersion: string;
			privacyVersion: string;
			/** The texts accepted were the deployment operator's. */
			platform: boolean;
	  }
	| {
			kind: "passkeySignIn";
			requestId: string;
			mediation?: "conditional";
			signal?: AbortSignal;
	  }
	| { kind: "enrollPasskey"; requestId: string }
	| { kind: "signOut"; requestId: string };

export const signAction$ = new Subject<SignAction>();

export interface SignResult {
	readonly requestId: string;
	readonly kind: SignAction["kind"];
	readonly ok: boolean;
	readonly error?: string | undefined;
	/** verifyOtp: server-derived — true when no `users/{uid}` doc existed yet. */
	readonly isNewUser?: boolean | undefined;
	/** verifyOtp — the Firebase-Auth uid the write seam resolved. */
	readonly uid?: string | undefined;
	/** verifyOtp — the Firebase-Auth profile display name, when one exists. */
	readonly displayName?: string | undefined;
	/** verifyOtp — the organization this sign-in opened. */
	readonly workspaceId?: string | undefined;
	/** resolveEmail — whether a code would be mailed to this address: the
	 *  one admission sendOTP runs (resolveEmail.api.ts). */
	readonly known?: boolean | undefined;
	/** resolveEmail, sendOtp — present only when the email holds a live
	 *  pending invite. */
	readonly invite?: InviteContext | undefined;
	/** sendOtp failure — `rate-limited` when this address asked for too many
	 *  codes (sendOTP.api.ts's `resource-exhausted`), so the view names waiting
	 *  as the remedy rather than an immediate retry.
	 *  verifyOtp failure — distinguishes "the invite backing this code has
	 *  since lapsed" (verifyOTP.api.ts's `failed-precondition/invite-expired`,
	 *  thrown only after the code itself was confirmed correct) from an
	 *  ordinary wrong/expired code, so the view can show the right copy. */
	readonly reason?: "invite-expired" | "rate-limited" | "cancelled" | undefined;
}

export const signResult$ = new Subject<SignResult>();
